import { ThemeEvents } from '@theme/events';

/**
 * Free travel size: pick + auto-remove.
 *
 * snippets/cart-free-travel.liquid renders the progress bar and picker, and
 * exposes data-free-travel-action="remove:<key>" when the tagged travel line
 * (properties._free_travel) is still in the cart although the subtotal has
 * dropped under settings.free_travel_threshold. Acting on it triggers a
 * cart:update, which re-renders and re-evaluates against the current cart.
 */

const ACTION_SELECTOR = '[data-free-travel-action]';

// Guards against acting twice when several cart:update events land close
// together; the next real cart:update re-checks the actual state.
let pending = false;

/**
 * @param {ParentNode} root
 * @returns {string}
 */
function readAction(root) {
  const el = root.querySelector(ACTION_SELECTOR);
  return el ? el.getAttribute('data-free-travel-action') || '' : '';
}

/**
 * @param {Record<string, string> | undefined} sections
 * @returns {string | null} the action found in any section carrying the marker, or null if none carry it
 */
function readActionFromSections(sections) {
  if (!sections) return null;

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || html.indexOf('data-free-travel-action') === -1) continue;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return readAction(doc);
  }

  return null;
}

function cartSectionIds() {
  /** @type {string[]} */
  const ids = [];

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  });

  return ids;
}

/**
 * @param {string} url
 * @param {object} body
 */
function postJSON(url, body) {
  return fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((response) => response.json());
}

async function fetchCartSections() {
  const sectionIds = cartSectionIds();
  if (sectionIds.length === 0) return undefined;

  const response = await fetch(`/?sections=${sectionIds.join(',')}`);
  return response.json();
}

/**
 * @param {Record<string, string> | undefined} sections
 * @param {number} [itemCount]
 */
function dispatchCartUpdate(sections, itemCount) {
  document.dispatchEvent(
    new CustomEvent(ThemeEvents.cartUpdate, {
      bubbles: true,
      detail: {
        data: {
          source: 'cart-free-travel',
          itemCount,
          sections,
        },
      },
    })
  );
}

/**
 * @param {string} action
 */
async function processAction(action) {
  if (pending || action.indexOf('remove:') !== 0) return;
  pending = true;

  try {
    const changeData = await postJSON('/cart/change.js', { id: action.slice(7), quantity: 0 });
    if (changeData.status) return; // Shopify error responses carry a `status` field

    const sections = await fetchCartSections();
    dispatchCartUpdate(sections, changeData.item_count);
  } finally {
    pending = false;
  }
}

/**
 * @param {Record<string, string> | undefined} sections
 */
function checkAndProcess(sections) {
  const action = sections ? readActionFromSections(sections) : readAction(document);
  if (action) processAction(action);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const sections = /** @type {CustomEvent} */ (event).detail?.data?.sections;
  checkAndProcess(sections);
});

document.addEventListener('click', async (event) => {
  const target = /** @type {Element} */ (event.target);
  const btn = target.closest('.js-travel-add');
  if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
  event.preventDefault();

  const variantId = btn.getAttribute('data-variant-id');
  if (!variantId) return;

  const originalText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Toevoegen...';

  try {
    const addData = await postJSON('/cart/add.js', {
      items: [{ id: variantId, quantity: 1, properties: { _free_travel: 'true' } }],
    });
    if (addData.status) throw new Error(addData.description || 'Fout bij toevoegen travel');

    const sections = await fetchCartSections();
    dispatchCartUpdate(sections, addData.items?.[0]?.quantity);
  } catch (err) {
    console.error('Kon travel niet toevoegen:', err);
    btn.textContent = 'Mislukt';
    setTimeout(() => {
      btn.textContent = originalText;
      btn.disabled = false;
    }, 1500);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => checkAndProcess(undefined), { once: true });
} else {
  checkAndProcess(undefined);
}
